import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  FolderIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline'

import { apiClient, Project } from '../../lib/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import AutomationSettings from '../../components/automation/AutomationSettings'

// Form validation schema
const projectSchema = yup.object({
  name: yup.string().required('Project name is required').min(3, 'Name must be at least 3 characters'),
  subreddits: yup.string().required('Subreddits are required').matches(/^[a-zA-Z0-9_,\s]+$/, 'Only letters, numbers, underscores, commas and spaces allowed'),
  keywords: yup.string().required('Keywords are required'),
  business_goal: yup.string().required('Business goal is required'),
  default_template_id: yup.string().nullable(),

  // Goal-specific fields (conditional validation)
  target_url: yup.string().when('business_goal', {
    is: (val: string) => ['lead_generation', 'product_promotion', 'app_signup'].includes(val),
    then: (schema) => schema
      .required('Target URL is required for this goal type')
      .url('Must be a valid URL (e.g., https://yoursite.com)'),
    otherwise: (schema) => schema.nullable()
  }),

  product_name: yup.string().when('business_goal', {
    is: (val: string) => ['lead_generation', 'product_promotion', 'app_signup'].includes(val),
    then: (schema) => schema
      .required('Product/service name is required for this goal type')
      .min(2, 'Name must be at least 2 characters'),
    otherwise: (schema) => schema.nullable()
  }),

  product_description: yup.string().when('business_goal', {
    is: (val: string) => ['lead_generation', 'product_promotion', 'app_signup'].includes(val),
    then: (schema) => schema
      .required('Product description is required for this goal type')
      .min(10, 'Description must be at least 10 characters')
      .max(500, 'Description must be less than 500 characters'),
    otherwise: (schema) => schema.nullable()
  }),

  key_features: yup.string().nullable(),
  key_benefits: yup.string().nullable(),
})

type ProjectFormData = yup.InferType<typeof projectSchema>

const ProjectsPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [selectedGoal, setSelectedGoal] = useState<string>('lead_generation')
  const [expandedAutomation, setExpandedAutomation] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Fetch projects
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.getProjects(),
    staleTime: 30000,
  })

  // Fetch response templates
  const { data: templatesData } = useQuery({
    queryKey: ['response-templates'],
    queryFn: () => apiClient.getResponseTemplates(),
    staleTime: 5 * 60 * 1000,
  })

  // Create project mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Project>) => apiClient.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })
      toast.success('Project created successfully!')
      setIsCreateModalOpen(false)
      reset()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create project')
    },
  })

  // Update project mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Project> }) =>
      apiClient.updateProject(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })
      toast.success('Project updated successfully!')
      setEditingProject(null)
      reset()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update project')
    },
  })

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })
      toast.success('Project deleted successfully!')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete project')
    },
  })

  // Form handling
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<ProjectFormData>({
    resolver: yupResolver(projectSchema),
  })

  // Watch business_goal to keep selectedGoal in sync
  const watchedBusinessGoal = watch('business_goal')

  const onSubmit = async (data: ProjectFormData) => {
    console.log('📝 Form data submitted:', data)

    const subredditsArray = data.subreddits.split(',').map(s => s.trim()).filter(s => s.length > 0)
    const keywordsArray = data.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0)

    // Build goal_config JSON for lead generation goals
    let goal_config: any = {}
    if (['lead_generation', 'product_promotion', 'app_signup'].includes(data.business_goal)) {
      console.log('🔍 Building goal_config for business_goal:', data.business_goal)
      console.log('🔍 Form field values:', {
        target_url: data.target_url,
        product_name: data.product_name,
        product_description: data.product_description,
        key_features: data.key_features,
        key_benefits: data.key_benefits,
      })

      goal_config = {
        target_url: data.target_url || '',
        product_name: data.product_name || '',
        product_description: data.product_description || '',
      }

      // Add optional fields if provided
      if (data.key_features) {
        goal_config.key_features = data.key_features.split(',').map(f => f.trim()).filter(f => f.length > 0)
      }
      if (data.key_benefits) {
        goal_config.key_benefits = data.key_benefits.split(',').map(b => b.trim()).filter(b => b.length > 0)
      }

      console.log('✅ Built goal_config:', goal_config)
    }

    // Always include goal_config if we have a goal that requires it, even if partially empty
    // This ensures the data is saved and can be edited later
    const shouldIncludeGoalConfig = ['lead_generation', 'product_promotion', 'app_signup'].includes(data.business_goal)

    const projectData = {
      name: data.name,
      subreddits: subredditsArray,
      keywords: keywordsArray,
      // Also send as target_subreddits for WordPress API compatibility
      target_subreddits: subredditsArray,
      business_goal: data.business_goal,
      goal_config: shouldIncludeGoalConfig ? goal_config : undefined,
      default_template_id: data.default_template_id ? parseInt(data.default_template_id) : undefined,
      status: 'active',
    }

    console.log('🎯 shouldIncludeGoalConfig:', shouldIncludeGoalConfig)
    console.log('🎯 Final goal_config being sent:', projectData.goal_config)

    console.log('🚀 Sending project data:', projectData)
    console.log('📊 Data details:', {
      subreddits: subredditsArray,
      keywords: keywordsArray,
      subredditCount: subredditsArray.length,
      keywordCount: keywordsArray.length
    })

    if (editingProject) {
      console.log('📝 Updating existing project:', editingProject.id)
      updateMutation.mutate({ id: editingProject.id, updates: projectData })
    } else {
      console.log('🆕 Creating new project')
      createMutation.mutate(projectData)
    }
  }

  // Keep selectedGoal in sync with form's business_goal value
  useEffect(() => {
    if (watchedBusinessGoal && watchedBusinessGoal !== selectedGoal) {
      console.log('🔄 Syncing selectedGoal with form value:', watchedBusinessGoal)
      setSelectedGoal(watchedBusinessGoal)
    }
  }, [watchedBusinessGoal, selectedGoal])

  // Effect to populate goal_config fields when editingProject changes
  // This is triggered when the edit modal opens with a project
  useEffect(() => {
    // Only run when we have an editingProject (modal is open for editing)
    if (!editingProject) {
      console.log('🔍 goal_config useEffect: No editingProject, skipping')
      return
    }

    // Check if we have goal_config with actual data (not just empty object)
    const hasGoalConfig = editingProject.goal_config &&
      typeof editingProject.goal_config === 'object' &&
      Object.keys(editingProject.goal_config).length > 0

    const goal = editingProject.business_goal || 'lead_generation'
    const needsGoalConfig = ['lead_generation', 'product_promotion', 'app_signup'].includes(goal)

    console.log('🔍 goal_config useEffect triggered:', {
      projectId: editingProject.id,
      projectName: editingProject.name,
      hasGoalConfig,
      goal,
      needsGoalConfig,
      selectedGoal,
      goalConfigKeys: editingProject.goal_config ? Object.keys(editingProject.goal_config) : [],
      goalConfigData: editingProject.goal_config
    })

    if (hasGoalConfig && needsGoalConfig) {
      console.log('🔄 useEffect: Will populate goal_config fields')
      console.log('  - target_url:', editingProject.goal_config.target_url)
      console.log('  - product_name:', editingProject.goal_config.product_name)
      console.log('  - product_description:', editingProject.goal_config.product_description)

      // Use setTimeout to ensure the conditional form fields are rendered
      // The fields are only shown when selectedGoal matches certain values
      setTimeout(() => {
        console.log('⏰ setTimeout: Setting form values now')

        // Set all goal_config fields
        setValue('target_url', editingProject.goal_config?.target_url || '')
        setValue('product_name', editingProject.goal_config?.product_name || '')
        setValue('product_description', editingProject.goal_config?.product_description || '')

        // Handle key_features - could be array or string
        if (Array.isArray(editingProject.goal_config?.key_features)) {
          setValue('key_features', editingProject.goal_config.key_features.join(', '))
        } else if (typeof editingProject.goal_config?.key_features === 'string') {
          setValue('key_features', editingProject.goal_config.key_features)
        } else {
          setValue('key_features', '')
        }

        // Handle key_benefits - could be array or string
        if (Array.isArray(editingProject.goal_config?.key_benefits)) {
          setValue('key_benefits', editingProject.goal_config.key_benefits.join(', '))
        } else if (typeof editingProject.goal_config?.key_benefits === 'string') {
          setValue('key_benefits', editingProject.goal_config.key_benefits)
        } else {
          setValue('key_benefits', '')
        }

        console.log('✅ Form values set for goal_config fields')
      }, 150) // Increased delay to ensure DOM is ready
    } else {
      console.log('⚠️ Skipping goal_config population:', {
        hasGoalConfig,
        needsGoalConfig,
        reason: !hasGoalConfig ? 'No goal_config data' : 'Goal type does not need goal_config'
      })
    }
  }, [editingProject, setValue]) // Removed selectedGoal from dependencies - we use editingProject.business_goal instead

  const handleEdit = (project: Project) => {
    console.log('🔧 Editing project:', project.name, project)
    console.log('🎯 goal_config received:', project.goal_config)
    console.log('🎯 goal_config type:', typeof project.goal_config)
    console.log('🎯 goal_config is truthy?', !!project.goal_config)

    // First set the editing project - this triggers the useEffect above
    setEditingProject(project)

    // Set basic form fields
    setValue('name', project.name)

    // Handle subreddits - should be array after API transformation
    let subredditsStr = ''
    if (Array.isArray(project.subreddits) && project.subreddits.length > 0) {
      subredditsStr = project.subreddits.join(', ')
    }
    setValue('subreddits', subredditsStr)

    // Handle keywords - should be array after API transformation
    let keywordsStr = ''
    if (Array.isArray(project.keywords) && project.keywords.length > 0) {
      keywordsStr = project.keywords.join(', ')
    }
    setValue('keywords', keywordsStr)

    // Set business_goal and template
    const businessGoal = project.business_goal || 'lead_generation'
    setValue('business_goal', businessGoal)
    setSelectedGoal(businessGoal) // This triggers re-render, then useEffect populates goal_config
    setValue('default_template_id', project.default_template_id ? String(project.default_template_id) : '')

    console.log('✅ Form populated with:', {
      subredditsStr,
      keywordsStr,
      subredditsArray: project.subreddits,
      keywordsArray: project.keywords,
      business_goal: businessGoal,
      default_template_id: project.default_template_id,
      goal_config: project.goal_config
    })
    setIsCreateModalOpen(true)
  }

  const handleDelete = (project: Project) => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(project.id)
    }
  }

  const toggleMonitoring = (project: Project) => {
    const newStatus = project.status === 'monitoring' ? 'active' : 'monitoring'
    updateMutation.mutate({
      id: project.id,
      updates: { status: newStatus }
    })
  }

  const closeModal = () => {
    setIsCreateModalOpen(false)
    setEditingProject(null)
    reset()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Failed to load projects</div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['projects'] })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your Reddit monitoring projects and track their performance.
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          New Project
        </button>
      </div>

      {/* Projects Grid */}
      {projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <FolderIcon className="h-8 w-8 text-blue-500" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{project.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          project.status === 'monitoring'
                            ? 'bg-green-100 text-green-800'
                            : project.status === 'active'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status === 'monitoring' && <EyeIcon className="w-3 h-3 mr-1" />}
                          {project.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(project)}
                      className="text-gray-400 hover:text-blue-500"
                      title="Edit project"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(project)}
                      className="text-gray-400 hover:text-red-500"
                      title="Delete project"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Subreddits</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(project.subreddits && Array.isArray(project.subreddits)) ? project.subreddits.map((subreddit) => (
                        <span
                          key={subreddit}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800"
                        >
                          r/{subreddit}
                        </span>
                      )) : (
                        <span className="text-xs text-gray-500">No subreddits</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">Keywords</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(project.keywords && Array.isArray(project.keywords)) ? project.keywords.slice(0, 3).map((keyword) => (
                        <span
                          key={keyword}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {keyword}
                        </span>
                      )) : (
                        <span className="text-xs text-gray-500">No keywords</span>
                      )}
                      {(project.keywords && Array.isArray(project.keywords) && project.keywords.length > 3) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                          +{project.keywords.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                        {project.metrics?.total_opportunities || 0} opportunities
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleMonitoring(project)}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${
                          project.status === 'monitoring'
                            ? 'bg-red-100 text-red-800 hover:bg-red-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        } transition-colors`}
                        disabled={updateMutation.isPending}
                      >
                        {project.status === 'monitoring' ? (
                          <>
                            <PauseIcon className="h-3 w-3 mr-1" />
                            Stop
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-3 w-3 mr-1" />
                            Start
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setExpandedAutomation(expandedAutomation === project.id ? null : project.id)}
                        className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                        title="Configure automation"
                      >
                        <Cog6ToothIcon className="h-3 w-3 mr-1" />
                        Automation
                      </button>
                    </div>
                  </div>

                  {/* Collapsible Automation Settings */}
                  {expandedAutomation === project.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 pt-4 border-t border-gray-200"
                    >
                      <AutomationSettings
                        projectId={parseInt(project.id)}
                        projectName={project.name}
                        onUpdate={() => {
                          queryClient.invalidateQueries({ queryKey: ['projects'] })
                          queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })
                        }}
                      />
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No projects yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first Reddit monitoring project.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Project
            </button>
          </div>
        </motion.div>
      )}

      {/* Create/Edit Project Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project Name
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., SaaS Lead Generation"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Subreddits
                  </label>
                  <input
                    {...register('subreddits')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., entrepreneur, startups, SaaS"
                  />
                  {errors.subreddits && (
                    <p className="mt-1 text-sm text-red-600">{errors.subreddits.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Separate multiple subreddits with commas
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Keywords
                  </label>
                  <input
                    {...register('keywords')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="e.g., lead generation, marketing, CRM"
                  />
                  {errors.keywords && (
                    <p className="mt-1 text-sm text-red-600">{errors.keywords.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Separate multiple keywords with commas
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Business Goal
                  </label>
                  <select
                    {...register('business_goal')}
                    onChange={(e) => setSelectedGoal(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="lead_generation">Lead Generation</option>
                    <option value="product_promotion">Product Promotion</option>
                    <option value="brand_awareness">Brand Awareness</option>
                    <option value="community_building">Community Building</option>
                    <option value="customer_support">Customer Support</option>
                    <option value="partnership_opportunities">Partnership Opportunities</option>
                  </select>
                  {errors.business_goal && (
                    <p className="mt-1 text-sm text-red-600">{errors.business_goal.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    This helps auto-select the best response template
                  </p>
                </div>

                {/* Goal-specific fields - only show for lead generation, product promotion, app signup */}
                {(selectedGoal === 'lead_generation' || selectedGoal === 'product_promotion' || selectedGoal === 'app_signup') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-blue-800">
                          Product/Service Information Required
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            To generate effective {selectedGoal === 'lead_generation' ? 'lead generation' : selectedGoal === 'product_promotion' ? 'product promotion' : 'signup'} comments,
                            the AI needs to know what product/service to mention and where to direct users.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Target URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Target URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('target_url')}
                        type="url"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="https://reddminer.com"
                      />
                      {errors.target_url && (
                        <p className="mt-1 text-sm text-red-600">{errors.target_url.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Where should users go? This URL will be mentioned in AI-generated comments
                      </p>
                    </div>

                    {/* Product Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Product/Service Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register('product_name')}
                        type="text"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g., ReddMiner"
                      />
                      {errors.product_name && (
                        <p className="mt-1 text-sm text-red-600">{errors.product_name.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        What is your product/service called?
                      </p>
                    </div>

                    {/* Product Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Product Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        {...register('product_description')}
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g., An AI-powered Reddit lead generation platform that helps SaaS companies discover and engage with potential customers automatically"
                      />
                      {errors.product_description && (
                        <p className="mt-1 text-sm text-red-600">{errors.product_description.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Describe what your product does in 1-2 sentences (10-500 characters)
                      </p>
                    </div>

                    {/* Key Features */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Key Features <span className="text-gray-500 font-normal">(optional)</span>
                      </label>
                      <textarea
                        {...register('key_features')}
                        rows={2}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g., AI comment generation, keyword monitoring, sentiment analysis, auto-scheduling"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        List main features (comma-separated). The AI will vary which features to mention.
                      </p>
                    </div>

                    {/* Key Benefits */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Key Benefits <span className="text-gray-500 font-normal">(optional)</span>
                      </label>
                      <textarea
                        {...register('key_benefits')}
                        rows={2}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g., saves 10+ hours per week, increases lead quality by 3x, reduces customer acquisition cost"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        List main benefits (comma-separated). The AI will vary which benefits to emphasize.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Default Response Template <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <select
                    {...register('default_template_id')}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Auto-select based on goal</option>
                    {templatesData?.data && templatesData.data.length > 0 ? (
                      <>
                        <optgroup label="⭐ System Templates">
                          {templatesData.data
                            .filter((t) => t.user_id === 0)
                            .map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name} ({template.tone})
                                {template.use_count > 0 ? ` - Used ${template.use_count}x` : ''}
                              </option>
                            ))}
                        </optgroup>
                        {templatesData.data.some((t) => t.user_id !== 0) && (
                          <optgroup label="📝 My Templates">
                            {templatesData.data
                              .filter((t) => t.user_id !== 0)
                              .map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name} ({template.tone})
                                  {template.use_count > 0 ? ` - Used ${template.use_count}x` : ''}
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </>
                    ) : (
                      <option disabled>Loading templates...</option>
                    )}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank to automatically use templates matching your goal
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isSubmitting || createMutation.isPending || updateMutation.isPending ? (
                      <div className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">
                          {editingProject ? 'Updating...' : 'Creating...'}
                        </span>
                      </div>
                    ) : (
                      editingProject ? 'Update Project' : 'Create Project'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectsPage